import React, { useState, useEffect, useCallback } from 'react'
import {
  Play,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Clock,
  Globe,
  Cpu,
  FileText,
  Copy,
  Check,
  ChevronDown,
  ChevronRight
} from 'lucide-react'
import clsx from 'clsx'
import * as adminApi from '../../services/adminApi'
import Toast, { ToastContainer } from '../../components/admin/Toast'

// Common languages for quick selection
const commonLanguages = [
  { code: 'en-US', name: 'English (US)' },
  { code: 'cs-CZ', name: 'Czech' },
  { code: 'de-DE', name: 'German' },
  { code: 'fr-FR', name: 'French' },
  { code: 'es-ES', name: 'Spanish' },
  { code: 'it-IT', name: 'Italian' },
  { code: 'pt-BR', name: 'Portuguese (BR)' },
  { code: 'pl-PL', name: 'Polish' },
  { code: 'nl-NL', name: 'Dutch' },
  { code: 'ru-RU', name: 'Russian' },
  { code: 'ja-JP', name: 'Japanese' },
  { code: 'zh-CN', name: 'Chinese (Simplified)' },
  { code: 'ko-KR', name: 'Korean' },
]

const TranslationTestPage = () => {
  // State for configs/accounts
  const [configs, setConfigs] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Form state
  const [sourceText, setSourceText] = useState('')
  const [sourceLang, setSourceLang] = useState('en-US')
  const [targetLang, setTargetLang] = useState('cs-CZ')
  const [context, setContext] = useState('General translation')
  const [selectedAccounts, setSelectedAccounts] = useState([])

  // Results state
  const [results, setResults] = useState([])
  const [isTranslating, setIsTranslating] = useState(false)

  // Toast state
  const [toasts, setToasts] = useState([])

  // Copy state
  const [copiedId, setCopiedId] = useState(null)

  // Expanded providers state
  const [expandedProviders, setExpandedProviders] = useState({})

  // Add toast helper
  const addToast = useCallback((message, type = 'info') => {
    const id = Date.now()
    setToasts(prev => [...prev, { id, message, type }])
  }, [])

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id))
  }, [])

  // Fetch configs with accounts
  const fetchConfigs = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await adminApi.getConfigs()
      setConfigs(data)

      // Expand all providers by default
      const expanded = {}
      data.forEach(config => {
        expanded[config.id] = true
      })
      setExpandedProviders(expanded)
    } catch (err) {
      console.error('Failed to fetch configs:', err)
      setError(err.response?.data?.detail || 'Failed to load configurations')
      addToast('Failed to load configurations', 'error')
    } finally {
      setLoading(false)
    }
  }, [addToast])

  useEffect(() => {
    fetchConfigs()
  }, [fetchConfigs])

  // Toggle account selection
  const toggleAccount = (accountId) => {
    setSelectedAccounts(prev => {
      if (prev.includes(accountId)) {
        return prev.filter(id => id !== accountId)
      }
      return [...prev, accountId]
    })
  }

  // Select all accounts for a provider
  const toggleProvider = (config) => {
    const accountIds = config.accounts?.filter(a => a.is_active).map(a => a.id) || []
    const allSelected = accountIds.every(id => selectedAccounts.includes(id))

    if (allSelected) {
      // Deselect all
      setSelectedAccounts(prev => prev.filter(id => !accountIds.includes(id)))
    } else {
      // Select all
      setSelectedAccounts(prev => [...new Set([...prev, ...accountIds])])
    }
  }

  // Toggle provider expansion
  const toggleExpand = (configId) => {
    setExpandedProviders(prev => ({
      ...prev,
      [configId]: !prev[configId]
    }))
  }

  // Run translation test
  const handleTranslate = async () => {
    if (!sourceText.trim()) {
      addToast('Please enter source text', 'error')
      return
    }

    if (selectedAccounts.length === 0) {
      addToast('Please select at least one account', 'error')
      return
    }

    setIsTranslating(true)
    setResults([])

    // Run translation for each selected account
    const newResults = []

    for (const accountId of selectedAccounts) {
      // Find the account info
      let accountInfo = null
      let configInfo = null
      for (const config of configs) {
        const account = config.accounts?.find(a => a.id === accountId)
        if (account) {
          accountInfo = account
          configInfo = config
          break
        }
      }

      if (!accountInfo || !configInfo) continue

      const startTime = Date.now()

      try {
        const response = await adminApi.testTranslation({
          texts: [sourceText],
          source_lang: sourceLang,
          target_lang: targetLang,
          account_ids: [accountId],
          context: context
        })

        const duration = Date.now() - startTime

        newResults.push({
          accountId,
          model: accountInfo.model,
          provider: configInfo.provider,
          providerName: configInfo.name,
          translation: response.translations?.[0] || 'No translation returned',
          duration,
          status: 'success'
        })
      } catch (err) {
        const duration = Date.now() - startTime
        newResults.push({
          accountId,
          model: accountInfo.model,
          provider: configInfo.provider,
          providerName: configInfo.name,
          translation: err.response?.data?.detail || err.message || 'Translation failed',
          duration,
          status: 'error'
        })
      }

      // Update results incrementally
      setResults([...newResults])
    }

    setIsTranslating(false)
    addToast(`Translation completed for ${newResults.length} account(s)`, 'success')
  }

  // Copy translation
  const handleCopy = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedId(id)
      setTimeout(() => setCopiedId(null), 2000)
    } catch (err) {
      addToast('Failed to copy', 'error')
    }
  }

  // Get provider color
  const getProviderColor = (provider) => {
    const colors = {
      openai: 'bg-green-500',
      anthropic: 'bg-orange-500',
      gemini: 'bg-blue-500',
      grok: 'bg-purple-500',
      deepseek: 'bg-cyan-500',
    }
    return colors[provider] || 'bg-gray-500'
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Translation Test Console
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Test translation with different LLM accounts side-by-side
        </p>
      </div>

      {/* Error state */}
      {error && (
        <div className="rounded-md bg-red-50 dark:bg-red-900/30 p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left panel - Input */}
        <div className="lg:col-span-2 space-y-4">
          {/* Language selection */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <Globe className="w-4 h-4 inline mr-1" />
                  Source Language
                </label>
                <select
                  value={sourceLang}
                  onChange={(e) => setSourceLang(e.target.value)}
                  className="block w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm focus:ring-primary-500 focus:border-primary-500"
                >
                  {commonLanguages.map(lang => (
                    <option key={lang.code} value={lang.code}>{lang.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <Globe className="w-4 h-4 inline mr-1" />
                  Target Language
                </label>
                <select
                  value={targetLang}
                  onChange={(e) => setTargetLang(e.target.value)}
                  className="block w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm focus:ring-primary-500 focus:border-primary-500"
                >
                  {commonLanguages.map(lang => (
                    <option key={lang.code} value={lang.code}>{lang.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Context */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                <FileText className="w-4 h-4 inline mr-1" />
                Translation Context
              </label>
              <input
                type="text"
                value={context}
                onChange={(e) => setContext(e.target.value)}
                placeholder="e.g., Technical documentation, Marketing copy..."
                className="block w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>

          {/* Source text input */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Source Text
            </label>
            <textarea
              value={sourceText}
              onChange={(e) => setSourceText(e.target.value)}
              rows={6}
              placeholder="Enter the text you want to translate..."
              className="block w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm focus:ring-primary-500 focus:border-primary-500 font-mono"
            />
            <div className="mt-2 flex justify-between items-center">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {sourceText.length} characters
              </span>
              <button
                onClick={handleTranslate}
                disabled={isTranslating || !sourceText.trim() || selectedAccounts.length === 0}
                className={clsx(
                  'inline-flex items-center px-4 py-2 rounded-md text-sm font-medium',
                  'text-white bg-primary-600 hover:bg-primary-700',
                  'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500',
                  'disabled:opacity-50 disabled:cursor-not-allowed'
                )}
              >
                {isTranslating ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Translating...
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4 mr-2" />
                    Run Translation
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Results */}
          {results.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                Results ({results.length})
              </h3>
              {results.map((result, index) => (
                <div
                  key={result.accountId}
                  className={clsx(
                    'bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4',
                    result.status === 'error' && 'border-l-4 border-red-500'
                  )}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <span className={clsx('w-2 h-2 rounded-full', getProviderColor(result.provider))} />
                      <span className="font-medium text-gray-900 dark:text-white">
                        {result.model}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        ({result.providerName})
                      </span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center">
                        <Clock className="w-3 h-3 mr-1" />
                        {(result.duration / 1000).toFixed(2)}s
                      </span>
                      {result.status === 'success' ? (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      ) : (
                        <AlertCircle className="w-4 h-4 text-red-500" />
                      )}
                      <button
                        onClick={() => handleCopy(result.translation, result.accountId)}
                        className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        title="Copy"
                      >
                        {copiedId === result.accountId ? (
                          <Check className="w-4 h-4 text-green-500" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>
                  <div className={clsx(
                    'p-3 rounded bg-gray-50 dark:bg-gray-900/50 text-sm font-mono whitespace-pre-wrap',
                    result.status === 'error' ? 'text-red-600 dark:text-red-400' : 'text-gray-700 dark:text-gray-300'
                  )}>
                    {result.translation}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right panel - Account selection */}
        <div className="space-y-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-900 dark:text-white flex items-center">
                <Cpu className="w-4 h-4 mr-2" />
                Select Accounts
              </h3>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {selectedAccounts.length} selected
              </span>
            </div>

            {loading ? (
              <div className="text-center py-4">
                <RefreshCw className="w-5 h-5 mx-auto text-gray-400 animate-spin" />
                <p className="mt-2 text-sm text-gray-500">Loading accounts...</p>
              </div>
            ) : configs.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                No providers configured
              </p>
            ) : (
              <div className="space-y-2">
                {configs.filter(c => c.is_active).map(config => {
                  const activeAccounts = config.accounts?.filter(a => a.is_active) || []
                  const allSelected = activeAccounts.length > 0 && activeAccounts.every(a => selectedAccounts.includes(a.id))
                  const someSelected = activeAccounts.some(a => selectedAccounts.includes(a.id))
                  const isExpanded = expandedProviders[config.id]

                  return (
                    <div key={config.id} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                      {/* Provider header */}
                      <div
                        className={clsx(
                          'flex items-center px-3 py-2 cursor-pointer',
                          'bg-gray-50 dark:bg-gray-900/50 hover:bg-gray-100 dark:hover:bg-gray-900'
                        )}
                        onClick={() => toggleExpand(config.id)}
                      >
                        <button className="mr-2 text-gray-400">
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4" />
                          ) : (
                            <ChevronRight className="w-4 h-4" />
                          )}
                        </button>
                        <input
                          type="checkbox"
                          checked={allSelected}
                          ref={el => {
                            if (el) el.indeterminate = someSelected && !allSelected
                          }}
                          onChange={(e) => {
                            e.stopPropagation()
                            toggleProvider(config)
                          }}
                          className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 mr-2"
                        />
                        <span className={clsx('w-2 h-2 rounded-full mr-2', getProviderColor(config.provider))} />
                        <span className="text-sm font-medium text-gray-900 dark:text-white flex-1">
                          {config.name}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {activeAccounts.length}
                        </span>
                      </div>

                      {/* Accounts list */}
                      {isExpanded && activeAccounts.length > 0 && (
                        <div className="divide-y divide-gray-100 dark:divide-gray-800">
                          {activeAccounts.map(account => (
                            <label
                              key={account.id}
                              className={clsx(
                                'flex items-center px-3 py-2 cursor-pointer',
                                'hover:bg-gray-50 dark:hover:bg-gray-700/50',
                                selectedAccounts.includes(account.id) && 'bg-primary-50 dark:bg-primary-900/20'
                              )}
                            >
                              <input
                                type="checkbox"
                                checked={selectedAccounts.includes(account.id)}
                                onChange={() => toggleAccount(account.id)}
                                className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 mr-3 ml-6"
                              />
                              <code className="text-xs bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded text-gray-700 dark:text-gray-300">
                                {account.model}
                              </code>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {/* Quick tips */}
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
            <h4 className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-2">
              Quick Tips
            </h4>
            <ul className="text-xs text-blue-600 dark:text-blue-400 space-y-1">
              <li>• Select multiple accounts to compare translations</li>
              <li>• Results show response time for each model</li>
              <li>• Use context to improve translation quality</li>
              <li>• Click copy button to copy individual results</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Toasts */}
      <ToastContainer>
        {toasts.map(toast => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </ToastContainer>
    </div>
  )
}

export default TranslationTestPage
